import { formatDateRu } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/store.js';
import { request } from '../../sync/api.js';
import { syncNow } from '../../sync/engine.js';
import { enqueue } from '../../sync/outbox.js';

/**
 * Отсутствия: заявки на согласование и отдельно — срывы смен.
 *
 * Плановые отгулы и экстренные невыходы показаны раздельно: это разные вещи,
 * и смешивать их в один список значит потерять смысл обеих цифр.
 */

interface Absence {
  id: string;
  user: { id: string; fullName: string };
  site: string;
  date: string;
  shiftNumber: number;
  type: 'PLANNED' | 'UNPLANNED';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string;
  createdAt: string;
}

const STATUS_LABEL: Record<Absence['status'], string> = {
  PENDING: 'ждёт решения',
  APPROVED: 'согласовано',
  REJECTED: 'отказано',
};

export function AbsencesScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const [absences, setAbsences] = useState<Absence[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await request<{ absences: Absence[] }>(
        '/api/absences',
        { method: 'GET' },
        20_000,
      );
      setAbsences(response.absences);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить заявки');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (absence: Absence, approved: boolean): Promise<void> => {
    if (!user) return;
    const comment = window.prompt(
      approved ? 'Комментарий (необязательно):' : 'Почему отказываете?',
      approved ? '' : '',
    );
    if (!approved && (comment === null || comment.trim().length === 0)) return;

    setBusy(absence.id);
    try {
      await enqueue({
        type: 'ABSENCE_DECIDED',
        aggregateType: 'ABSENCE',
        aggregateId: absence.id,
        userId: user.id,
        payload: {
          absenceId: absence.id,
          approved,
          comment: comment && comment.trim() !== '' ? comment.trim() : null,
        },
      });
      await syncNow();
      await load();
    } finally {
      setBusy(null);
    }
  };

  if (!absences) {
    return (
      <div className="min-h-screen px-4 py-6">
        <p className="text-lg text-dim">{error ?? 'Загружаю…'}</p>
        <Link to="/admin" className="mt-4 inline-block rounded-xl bg-muted px-4 py-3 text-base">
          Назад
        </Link>
      </div>
    );
  }

  const pending = absences.filter((item) => item.status === 'PENDING');
  const unplanned = absences.filter((item) => item.type === 'UNPLANNED');
  const decided = absences.filter((item) => item.status !== 'PENDING');

  const card = (absence: Absence, withButtons: boolean): JSX.Element => (
    <li
      key={absence.id}
      className={`rounded-xl p-4 ${absence.type === 'UNPLANNED' ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200' : 'bg-card shadow-card'}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-lg font-semibold">{absence.user.fullName}</span>
        <span className="text-base text-dim">
          {absence.site} · {formatDateRu(absence.date)} · смена {absence.shiftNumber}
        </span>
      </div>
      <p className="mt-2 text-base text-body">
        {absence.type === 'UNPLANNED' ? 'Экстренный невыход' : 'Плановая заявка'} ·{' '}
        {STATUS_LABEL[absence.status]}
      </p>
      <p className="mt-1 text-base">Причина: {absence.reason}</p>

      {withButtons && (
        <div className="mt-4 flex flex-wrap gap-touch">
          <button
            type="button"
            disabled={busy === absence.id}
            onClick={() => void decide(absence, true)}
            className="min-h-touch rounded-xl bg-emerald-600 px-5 text-base font-semibold text-white active:bg-emerald-700 disabled:opacity-40"
          >
            Согласовать
          </button>
          <button
            type="button"
            disabled={busy === absence.id}
            onClick={() => void decide(absence, false)}
            className="min-h-touch rounded-xl bg-muted px-5 text-base font-semibold active:bg-line disabled:opacity-40"
          >
            Отказать
          </button>
        </div>
      )}
    </li>
  );

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Отсутствия</h1>
        <Link to="/admin" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold">Ждут решения ({pending.length})</h2>
        {pending.length === 0 ? (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Заявок нет</p>
        ) : (
          <ul className="flex flex-col gap-touch">{pending.map((item) => card(item, true))}</ul>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold">Срывы смен ({unplanned.length})</h2>
        <p className="mb-3 text-base text-dim">
          Экстренные невыходы за всё время — по ним считается надёжность сотрудников
        </p>
        {unplanned.length === 0 ? (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Срывов не было</p>
        ) : (
          <ul className="flex flex-col gap-touch">
            {unplanned.map((item) => card(item, item.status === 'PENDING'))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Решённые ({decided.length})</h2>
        <ul className="flex flex-col gap-touch">
          {decided.slice(0, 20).map((item) => card(item, false))}
        </ul>
      </section>
    </div>
  );
}
